import { datadogRum } from "@datadog/browser-rum";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  type LoaderFunction,
  matchPath,
  Navigate,
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useRouteError,
} from "react-router-dom";

import {
  getReferralCodeFromUrl,
  openLinkToMobileApp,
  setIdleTimeInTimestamp,
  showViewAppFooter,
} from "@/services/DeepLinkService";
import {
  getAuthClaims,
  getPlatform,
  getPlatformCurrent,
  getPlatformLocation,
  getUser,
  getUserPreferences,
} from "api/methods";
import { usePlatformDomainTokens } from "hooks/usePlatformDomainTokens";
import {
  isAnonymousBrowsing,
  isAnonymousUser,
  useIsAnonymousBrowsing,
} from "modules/anonymousBrowsing/anonymousBrowsingState";
import {
  consumePostLoginReturnTo,
  isAnonymousBrowsablePath,
  isOnboardingDeferredForCheckout,
  isSessionlessExternalReturnPath,
  redirectToSignIn,
  setPostLoginReturnTo,
} from "modules/anonymousBrowsing/anonymousBrowsingSession";
import { AdonisChatProvider } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import AppIconListener from "modules/appIcon/AppIconListener/AppIconListener";
import { PushNotificationProvider, pushNotificationService } from "modules/pushNotifications";
import { publicStorefrontPaths } from "modules/storefront/publicStorefrontRoute";
import TopNav from "molecules/navbars/TopNav";
import { DesktopHeader } from "molecules/navigation/desktop-header/DesktopHeader";
import { Footer } from "molecules/navigation/footer/Footer";
import { SiteFooter } from "molecules/navigation/site-footer/SiteFooter";
import { ViewAppFooter } from "molecules/view-app-footer/ViewAppFooter";
import { AccessTemporarilySuspendedModal } from "organisms/access-temporarily-suspended-modal/AccessTemporarilySuspendedModal";
import { DeactivationPendingModal } from "organisms/deactivation-pending-modal/DeactivationPendingModal";
import { DisputeInProgressModal } from "organisms/dispute-in-progress-modal/DisputeInProgressModal";
import { completeAnonymousHandover } from "modules/anonymousCheckout/completeAnonymousHandover";
import { queryClient } from "pages/Root";
import RootError from "pages/RootError";
import { identifyFlagPlatform, identifyFlagUser, useFeatureFlag } from "services/Flags";
import { getBookAppointmentAction } from "services/HelperService";
import { currencyService } from "toolbox/CurrencyService";
import { waitObj } from "toolbox/Promise";
import { changeDayjsLocale } from "utils/dayjsUtils";
import { clearAcknowledgedPopupPromotionIds } from "utils/functions";
import { isNativeMobileApp } from "utils/platform.helpers";

import { AnonymousLocationPrompt } from "./AnonymousLocationPrompt";
import { PendingRouteViewport } from "./PendingRouteViewport";
import { CartPendingContent } from "./cart/CartPendingContent";
import {
  PendingMembershipPdp,
  PendingPackagePdp,
  PendingProductPdp,
  PendingProductsList,
  ShopPdpSkeleton,
} from "./shop/ShopListingSkeleton";

// growthbook flags refresh the router while we are getting
// ready for v3. This load runs inside the router, which in
// turn refrreshes the flags -> causing a loop.
const previousLoadUserRef = {
  id: "",
};

const load = async () => {
  const platform = queryClient.fetchQuery({
    ...getPlatformCurrent.keyed(),
    queryFn: () => getPlatformCurrent().catch(() => null),
  });

  const user = queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 0,
  });

  void queryClient.prefetchQuery({
    ...getUserPreferences.keyed(),
    staleTime: 0,
  });

  const platformLocation = queryClient.fetchQuery({
    ...getPlatformLocation.keyed(),
    queryFn: () =>
      getPlatformLocation().catch((err) => {
        if (isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }),
  });

  const authClaims = queryClient.ensureQueryData({ ...getAuthClaims.keyed() });

  return waitObj({
    user: user.then(async (userData) => {
      queryClient.setQueryDefaults(getUser.key(), {
        staleTime:
          // No staleTime if pre-registered, otherwise 2 minutes
          !userData.completedProfile && userData.hasBeenPreRegistered ? 0 : 1000 * 60 * 2,
      });

      if (isAnonymousUser(userData)) {
        // A signed-out shopper is identified by the practice, so practice-split experiments include them
        identifyFlagPlatform(await platform);
        return userData;
      }

      datadogRum.setUser({
        id: userData.id,
        name: userData.phoneNumber,
        email: userData.email ?? undefined,
        platformId: userData.platform?.id ?? undefined,
      });

      if (previousLoadUserRef.id !== userData.id) {
        identifyFlagUser(
          {
            id: userData.id,
            name: userData.fullName ?? undefined,
            email: userData.email ?? undefined,
          },
          userData.platform && {
            id: userData.platform.id,
            domain: userData.platformDomain?.domain ?? undefined,
            created: dayjs(userData.platform.created).toDate(),
          },
        );
      }

      return userData;
    }),
    platform: platform.then((platformData) => {
      changeDayjsLocale(platformData?.stripeRegion);
      currencyService.setCurrency(platformData?.stripeRegion);
      return platformData;
    }),
    platformLocation,
    authClaims,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;

const getIncompleteProfileRedirect = (user: LoaderData["user"]): string | null => {
  if (!user.firstName || !user.lastName) return "/signin/name";
  if (!user.email) return "/signin/email";
  if (!user.gender) return "/signin/gender";
  if (!user.signUpSource) return "/signin/source";
  return null;
};

export const appLayoutLoader: LoaderFunction = async ({ request }) => {
  const { pathname, search, searchParams } = new URL(request.url);

  // No session at all here, so none of load()'s auth-dependent fetches may run.
  if (isSessionlessExternalReturnPath(pathname, search)) {
    return null;
  }

  const data = await load();
  const { user, platform, platformLocation, authClaims } = data;

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;
  const isReturningFromSignIn = searchParams.get("fromSignIn") === "true";

  // Handle redirects for deactivated platforms
  // Note: For native platforms, we only redirect if the user is not already on a signin route.
  // This prevents redirect loops when the native app shell navigates to /signin routes.
  // This pattern matches the behavior in AnonymousLayout, PublicReferralRewardLayout,
  // PublicGiftCardLayout, and AuthenticatedOnboardingLayout.
  if (!isAdminImpersonation && platform?.active === false) {
    if ((isNativeMobileApp() && !request.url.includes("/signin")) || !isNativeMobileApp()) {
      return redirect("/deactivated");
    }

    return null;
  }

  // Anonymous visitors may only browse the catalogue; account routes go to
  // sign-in and remember where to come back to.
  if (isAnonymousBrowsing() && !isAnonymousBrowsablePath(pathname)) {
    return redirectToSignIn(pathname + search);
  }

  // Ensure the user has a platform location before allowing profile completion redirects.
  // Redirect loops are avoided by not redirecting while already on /signin routes.
  if (
    !isAdminImpersonation &&
    !user.isGuest &&
    !pathname.startsWith("/signin") &&
    platformLocation == null
  ) {
    return redirect("/signin/location");
  }

  // The deferral only covers checkout pages, so a patient who finishes the order or walks away
  // from it still meets this gate on their next navigation — the questions move, they do not vanish.
  if (
    !isAdminImpersonation &&
    !user.isGuest &&
    !user.completedProfile &&
    !user.hasBeenPreRegistered &&
    !isOnboardingDeferredForCheckout(pathname)
  ) {
    const profileRedirect = getIncompleteProfileRedirect(user);
    if (profileRedirect) return redirect(profileRedirect);
  }

  // Complete the anonymous handover, then honor stored return paths only for
  // existing-user sign-in; fresh onboarding consumes them and stays on Home.
  if (!isAnonymousUser(user) && !isAdminImpersonation && platform) {
    // Signing in anywhere but the checkout page lands here. Same handover either way, or an offer
    // claimed while logged out would be dropped without ever being applied or explained.
    if (!user.isGuest) {
      await completeAnonymousHandover(platform.id);
    }
    const returnTo = consumePostLoginReturnTo();
    if (isReturningFromSignIn && returnTo && returnTo !== pathname + search) {
      return redirect(returnTo);
    }
  }

  return data;
};

/**
 * A navigation onto the products list from another page. The list's own filters and sort rewrite
 * only its search, and those revalidate in place under the list.
 */
export const isPendingProductsList = (pendingPath: string | undefined, currentPath: string) =>
  !!pendingPath &&
  pendingPath !== currentPath &&
  !!matchPath(publicStorefrontPaths.productList, pendingPath);

/**
 * Layout route for authenticated users (post-login)
 */
const AppLayout = () => {
  const { user, platform, platformLocation, authClaims } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useNavigation();
  const pendingPath = navigation.state === "loading" ? navigation.location?.pathname : undefined;
  const pendingPackageId = pendingPath
    ? matchPath("/pdp/package/:id", pendingPath)?.params.id
    : undefined;
  const pendingMembershipId = pendingPath
    ? matchPath({ path: "/pdp/membership/:id", end: false }, pendingPath)?.params.id
    : undefined;
  const pendingProductId = pendingPath
    ? matchPath("/pdp/product/:id", pendingPath)?.params.id
    : undefined;
  const pendingPdp = !!pendingPath?.startsWith("/pdp/");
  const pendingProductsList = isPendingProductsList(pendingPath, location.pathname);
  const pendingCart = pendingPath === "/cart";
  const isAnonymousBrowsingActive = useIsAnonymousBrowsing();
  const isDesktopExperience = useFeatureFlag("desktopExperience");
  const referralCode = getReferralCodeFromUrl(globalThis.location.href);
  const mobileAppDiscovery = platform?.platformSettings?.mobileAppDiscovery;
  const [displayViewAppFooter, setDisplayViewAppFooter] = useState(
    mobileAppDiscovery && showViewAppFooter(user?.id),
  );

  const { platformDomainTokens } = usePlatformDomainTokens();

  useEffect(() => {
    if (displayViewAppFooter) {
      setDisplayViewAppFooter(showViewAppFooter(user?.id));
    }
  }, [displayViewAppFooter, user?.id]);

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;
  const isPlatformDeactivationPending =
    !isAdminImpersonation && platform?.status === "DeactivationPending";

  // Dispute modal logic
  const hasActiveDispute = !isAdminImpersonation && !!user.hasActiveDispute;
  const hasPartialDisputePendingAdminAction =
    !isAdminImpersonation && !!user.hasPartialDisputePendingAdminAction;
  const isOnPlatformSelector = location.pathname.startsWith("/platform-selector");
  const showDisputeModal = hasActiveDispute && !isOnPlatformSelector;
  const showSuspendedModal = hasPartialDisputePendingAdminAction && !isOnPlatformSelector;
  const platformName = platform?.name ?? user.platform?.name ?? "";
  const contactPhone = user.platformLocation?.phoneNumber ?? platformLocation?.phoneNumber;
  const contactEmail = user.platformLocation?.email ?? platformLocation?.email;
  const showSwitchLocations = (platformDomainTokens?.length ?? 0) > 1;

  const contactUrl = getBookAppointmentAction(
    null,
    platformLocation?.phoneNumber,
    platformLocation?.email,
  );

  const pendingPdpOverlay = pendingPackageId ? (
    <PendingPackagePdp listingId={pendingPackageId} />
  ) : pendingMembershipId ? (
    <PendingMembershipPdp membershipId={pendingMembershipId} />
  ) : pendingProductId ? (
    <PendingProductPdp productId={pendingProductId} />
  ) : pendingPdp ? (
    <ShopPdpSkeleton />
  ) : null;

  // Sibling navigations do not re-run this pathless layout's loader, so the
  // account-only guard also has to hold at render time.
  if (isAnonymousBrowsingActive && !isAnonymousBrowsablePath(location.pathname)) {
    setPostLoginReturnTo(location.pathname + location.search);
    return <Navigate replace to="/signin" />;
  }

  return (
    <PushNotificationProvider
      isNativeMobileApp={isNativeMobileApp()}
      pushNotificationService={pushNotificationService}
    >
      <AdonisChatProvider platformSettings={platform?.platformSettings}>
        <AppIconListener />
        <AnonymousLocationPrompt />
        <DeactivationPendingModal
          open={isPlatformDeactivationPending}
          platformName={platform?.name ?? ""}
          contactUrl={contactUrl}
        />
        <DisputeInProgressModal
          open={showDisputeModal}
          platformName={platformName}
          contactPhone={contactPhone}
          contactEmail={contactEmail}
          showSwitchLocations={showSwitchLocations}
          onSwitchLocations={() => navigate("/platform-selector")}
        />
        <AccessTemporarilySuspendedModal
          open={showSuspendedModal}
          platformName={platformName}
          contactPhone={contactPhone}
          contactEmail={contactEmail}
          showSwitchLocations={showSwitchLocations}
          onSwitchLocations={() => navigate("/platform-selector")}
        />
        {displayViewAppFooter && (
          <ViewAppFooter
            platformName={platform?.name || ""}
            open={displayViewAppFooter}
            onClose={() => {
              setDisplayViewAppFooter(false);
              setIdleTimeInTimestamp(user?.id);
            }}
            onOpenApp={() => openLinkToMobileApp(platform?.id, referralCode)}
          />
        )}
        {isDesktopExperience && <DesktopHeader />}
        <div
          className="flex flex-auto pb-[calc(59px_+_env(safe-area-inset-bottom))] desk:pb-0"
          data-testid="app-layout-content"
        >
          <div className="w-full">
            <Outlet />
          </div>
        </div>
        {pendingPdp && pendingPdpOverlay && (
          <PendingRouteViewport>{pendingPdpOverlay}</PendingRouteViewport>
        )}
        {pendingProductsList && (
          <PendingRouteViewport>
            <PendingProductsList />
          </PendingRouteViewport>
        )}
        {pendingCart && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-white">
            <CartPendingContent />
          </div>
        )}
        {isDesktopExperience && <SiteFooter />}
        <div
          className="fixed bottom-0 left-0 right-0 z-40 flex justify-center desk:hidden"
          data-testid="app-layout-mobile-footer"
        >
          <div className={isDesktopExperience ? "w-full" : "w-full sm:w-[390px]"}>
            <Footer />
          </div>
        </div>
      </AdonisChatProvider>
    </PushNotificationProvider>
  );
};

export const AppLayoutError = () => {
  const error = useRouteError();
  const location = useLocation();
  const isDesktopExperience = useFeatureFlag("desktopExperience");

  const { data: logo } = useQuery({
    ...getPlatform.keyed(),
    select: (data) => data.logoImageUrl,
  });

  if (isAxiosError(error) && error.response?.status === 401) {
    clearAcknowledgedPopupPromotionIds();
    setPostLoginReturnTo(location.pathname + location.search);
    return <Navigate to="/signin" />;
  }

  return (
    <>
      <DesktopHeader />
      <div className="w-full pb-[calc(59px_+_env(safe-area-inset-bottom))] desk:pb-0">
        <TopNav>{logo && <img src={logo} className="h-8" />}</TopNav>
        <RootError />
      </div>
      <div className="fixed bottom-0 left-0 right-0 z-40 flex justify-center desk:hidden">
        <div className={isDesktopExperience ? "w-full" : "w-full sm:w-[390px]"}>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default AppLayout;
